import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionService, { SubscriptionTier, Subscription } from '../services/SubscriptionService';
import { useAuth } from '../contexts/AuthContext';
import './Pricing.css';

const Pricing: React.FC = () => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [tiersData, subData] = await Promise.all([
        SubscriptionService.getTiers(),
        user ? SubscriptionService.getCurrentSubscription().catch(() => null) : Promise.resolve(null)
      ]);
      setTiers(tiersData);
      setCurrentSubscription(subData);
    } catch (error) {
      console.error('Failed to load pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (tier: SubscriptionTier) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (tier.name === 'free') {
      alert('You are already on the Free plan!');
      return;
    }

    if (currentSubscription && currentSubscription.tier.name === tier.name) {
      alert('You are already subscribed to this plan!');
      return;
    }

    try {
      setLoading(true);
      await SubscriptionService.createSubscriptionCheckout(tier.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const isCurrentTier = (tierName: string) => {
    return currentSubscription?.tier.name === tierName;
  };

  const getTierBadge = (tierName: string) => {
    if (tierName === 'elite') return '👑 BEST VALUE';
    if (tierName === 'premium') return '⭐ POPULAR';
    return '';
  };

  if (loading) {
    return <div className="pricing-container"><div className="loading">Loading pricing...</div></div>;
  }

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Find your perfect Loki match with the right subscription</p>
      </div>

      <div className="pricing-tiers">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`pricing-card ${tier.name} ${isCurrentTier(tier.name) ? 'current' : ''}`}
          >
            {getTierBadge(tier.name) && (
              <div className="tier-badge">{getTierBadge(tier.name)}</div>
            )}

            {isCurrentTier(tier.name) && (
              <div className="current-plan-badge">Current Plan</div>
            )}

            <div className="tier-header">
              <h2>{tier.displayName}</h2>
              <div className="tier-price">
                {tier.price === 0 ? (
                  <span className="price">Free</span>
                ) : (
                  <>
                    <span className="currency">$</span>
                    <span className="price">{tier.price.toFixed(0)}</span>
                    <span className="period">/month</span>
                  </>
                )}
              </div>
            </div>

            <ul className="tier-features">
              {tier.features.map((feature, index) => (
                <li key={index}>
                  <span className="checkmark">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              className={`select-button ${isCurrentTier(tier.name) ? 'current' : ''}`}
              onClick={() => handleSelectPlan(tier)}
              disabled={isCurrentTier(tier.name) || loading}
            >
              {isCurrentTier(tier.name) ? 'Current Plan' : tier.name === 'free' ? 'Start Free' : 'Upgrade Now'}
            </button>
          </div>
        ))}
      </div>

      <div className="pricing-footer">
        <p>All plans include secure payments via Stripe</p>
        <p>Cancel anytime • No hidden fees • 24/7 Support</p>
      </div>
    </div>
  );
};

export default Pricing;
